import React from 'react';
import { motion } from 'framer-motion';
import { Users, Award, Target, Heart, CheckCircle2, Phone, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const AboutUs = () => {
  const values = [
    {
      icon: Award,
      title: 'Qualität',
      description: 'Höchste Qualitätsstandards bei jedem Projekt, vom ersten Pinselstrich bis zur finalen Abnahme.'
    },
    {
      icon: Target,
      title: 'Präzision',
      description: 'Schweizer Präzision in jedem Detail – termingerechte und saubere Ausführung garantiert.'
    },
    {
      icon: Heart,
      title: 'Leidenschaft',
      description: 'Wir lieben was wir tun. Jedes Projekt wird mit Hingabe und Professionalität umgesetzt.'
    },
    {
      icon: Users,
      title: 'Teamwork',
      description: '8 erfahrene Fachkräfte arbeiten Hand in Hand für Ihren Erfolg.'
    }
  ];

  const features = [
    'Kostenlose Beratung vor Ort',
    'Transparente Preisgestaltung',
    'Termingerechte Fertigstellung',
    'Qualitätsgarantie auf alle Arbeiten',
    'Umfassende Projektbetreuung',
    'Professionelle Umsetzung'
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 bg-[#003B7A] text-white" data-testid="about-hero">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl"
          >
            <p className="font-body text-xs tracking-[0.3em] uppercase text-[#FFD700] mb-6" data-testid="about-subtitle">
              Über uns
            </p>
            <h1 className="font-heading font-extrabold text-5xl md:text-7xl tracking-tight leading-[1.1] mb-8" data-testid="about-title">
              IHR PARTNER FÜR
              <span className="block gold-shimmer mt-2">BAU & RENOVATION</span>
            </h1>
            <p className="font-body text-lg md:text-xl leading-relaxed text-gray-300" data-testid="about-description">
              Marali Immobilien AG ist Ihr zuverlässiger Experte für hochwertige Bau- und Renovationsarbeiten in Cazis und ganz Graubünden. Mit Leidenschaft, Präzision und jahrelanger Erfahrung verwirklichen wir Ihre Projekte.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-24 md:py-32 bg-white" data-testid="company-story">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight mb-6" data-testid="story-title">
                Erfahrung trifft Innovation
              </h2>
              <div className="space-y-6 font-body text-base md:text-lg leading-relaxed text-gray-700">
                <p>
                  Seit Jahren sind wir in der Region Graubünden tätig und haben uns einen exzellenten Ruf erarbeitet. Unser Team besteht aus 8 hochqualifizierten Fachkräften, die mit Leidenschaft und Präzision an jedem Projekt arbeiten.
                </p>
                <p>
                  Von klassischen Malerarbeiten über moderne Trockenbau-Lösungen bis hin zu komplexen Fassadenprojekten – wir bieten das komplette Spektrum professioneller Bauleistungen aus einer Hand.
                </p>
                <p>
                  Unser Versprechen: Schweizer Qualität, termingerechte Ausführung und faire Preise. Wir setzen auf langfristige Kundenbeziehungen und überzeugen durch exzellente Arbeit.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src="https://images.unsplash.com/photo-1758548157747-285c7012db5b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtb2Rlcm4lMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMHJlbm92YXRpb24lMjBicmlnaHR8ZW58MHx8fHwxNzczNzM2NTQ4fDA&ixlib=rb-4.1.0&q=85"
                alt="Modern Interior"
                className="w-full h-[500px] object-cover shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32 bg-[#F5F5F7]" data-testid="values-section">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="font-body text-xs tracking-[0.3em] uppercase text-[#FFD700] mb-4" data-testid="values-subtitle">
              Unsere Werte
            </p>
            <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight mb-6" data-testid="values-title">
              Wofür wir stehen
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-8 text-center hover:shadow-xl transition-shadow"
                  data-testid={`value-${index}`}
                >
                  <Icon className="w-12 h-12 text-[#FFD700] mx-auto mb-6" />
                  <h3 className="font-heading font-bold text-xl mb-4">{value.title}</h3>
                  <p className="font-body text-sm text-gray-600 leading-relaxed">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="team-section">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="font-body text-xs tracking-[0.3em] uppercase text-[#FFD700] mb-4" data-testid="team-subtitle">
              Unser Team
            </p>
            <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight mb-6" data-testid="team-title">
              8 Experten, ein Ziel: Ihre Zufriedenheit
            </h2>
            <p className="font-body text-base md:text-lg leading-relaxed text-gray-600 max-w-3xl mx-auto">
              Unser Team besteht aus hochqualifizierten Fachkräften mit jahrelanger Erfahrung in allen Bereichen des Bau- und Renovationsgewerbes. Jedes Mitglied bringt spezielle Expertise mit und gemeinsam garantieren wir höchste Qualität.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-4xl mx-auto">
            <div className="bg-[#F5F5F7] p-8">
              <Users className="w-10 h-10 text-[#FFD700] mb-4" />
              <h3 className="font-heading font-bold text-2xl mb-3">Erfahrene Fachkräfte</h3>
              <p className="font-body text-base text-gray-700 leading-relaxed">
                Jedes Teammitglied verfügt über mehrjährige Erfahrung und regelmäßige Weiterbildungen in seinem Fachbereich.
              </p>
            </div>
            <div className="bg-[#F5F5F7] p-8">
              <Award className="w-10 h-10 text-[#FFD700] mb-4" />
              <h3 className="font-heading font-bold text-2xl mb-3">Zertifizierte Qualität</h3>
              <p className="font-body text-base text-gray-700 leading-relaxed">
                Wir arbeiten nach den höchsten Qualitätsstandards und setzen auf kontinuierliche Verbesserung unserer Prozesse.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 md:py-32 bg-[#003B7A] text-white" data-testid="why-choose-section">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight mb-8" data-testid="why-choose-title">
                Warum Marali Immobilien AG?
              </h2>
              <ul className="space-y-4">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-4" data-testid={`feature-item-${index}`}>
                    <CheckCircle2 className="w-6 h-6 text-[#FFD700] flex-shrink-0" />
                    <span className="font-body text-base">{feature}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white text-[#003B7A] p-12"
            >
              <h3 className="font-heading font-bold text-3xl mb-6">Bereit zu starten?</h3>
              <p className="font-body text-base text-gray-700 mb-8 leading-relaxed">
                Kontaktieren Sie uns für eine kostenlose Beratung. Wir freuen uns auf Ihr Projekt!
              </p>
              <div className="space-y-4">
                <Link
                  to="/kontakt"
                  className="block w-full bg-[#FFD700] text-white px-8 py-4 text-sm font-bold tracking-widest uppercase hover:bg-[#c19d2f] transition-all duration-300 text-center"
                  data-testid="why-choose-contact-cta"
                >
                  Jetzt Kontakt aufnehmen
                </Link>
                <a
                  href="tel:+41792649063"
                  className="flex items-center justify-center gap-2 w-full bg-[#003B7A] text-white px-8 py-4 text-sm font-bold tracking-widest uppercase hover:bg-[#1a1a1a] transition-all duration-300"
                  data-testid="why-choose-phone-cta"
                >
                  <Phone className="w-5 h-5" />
                  +41 79 264 90 63
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;